import React, { useEffect, useState } from "react";
import { Panel, Badge, EmptyState } from "./BuyerPortal";
import PropertyForm from "./PropertyForm";
import { ImageUploader, GalleryUploader } from "@/components/ImageUploaders";
import { fmtDate } from "@/components/AuctionCard";
import { BrandLogo } from "@/components/BrandCard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Star, X, Check } from "lucide-react";

const AUCTION_PRESET = { listing_type: "auction", legal_status: "not_started", builder_authorized: false, price_match_assured: false, bhk: null, bathrooms: null, parking: null };
const btn = "text-[10px] px-2 py-1 border inline-flex items-center gap-1";

function StarToggle({ on, onClick, testid, title }) {
    return (
        <button onClick={onClick} data-testid={testid} title={title}
            className={`w-8 h-8 inline-flex items-center justify-center border transition-colors ${on ? "bg-[var(--ebg-gold)] border-[var(--ebg-gold)] text-black" : "bg-white border-black/15 text-black/30 hover:border-[var(--ebg-gold)] hover:text-[var(--ebg-gold)]"}`}>
            <Star size={14} fill={on ? "currentColor" : "none"}/>
        </button>
    );
}

function NewButton({ onClick, testid, children }) {
    return (
        <button onClick={onClick} data-testid={testid} className="bg-black text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black inline-flex items-center gap-2">
            <Plus size={13}/> {children}
        </button>
    );
}

export function AuctionsPanel() {
    const [items, setItems] = useState([]);
    const [editing, setEditing] = useState(null);
    const [creating, setCreating] = useState(false);
    const load = () => api.get("/properties", { params: { listing_type: "auction", limit: 200 } }).then((r) => setItems(r.data.items || [])).catch(() => {});
    useEffect(() => { load(); }, []);

    const openEdit = async (slug) => {
        try { setEditing((await api.get(`/admin/properties/${slug}`)).data); } catch (e) { toast.error(formatApiError(e)); }
    };
    const del = async (p) => {
        if (!window.confirm(`Delete auction "${p.title}"?`)) return;
        try { await api.delete(`/admin/properties/${p.id}`); toast.success("Auction deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
    };
    const feature = async (p) => {
        try { await api.patch(`/admin/properties/${p.id}/featured`, { featured: !p.featured }); load(); } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <Panel title={`Auction Properties (${items.length})`} cta={{ to: "/auction-properties", l: "View Public Page" }} testid="admin-auctions">
                <div className="flex items-center justify-between mb-4 -mt-2 gap-3 flex-wrap">
                    <div className="text-[11px] text-black/55">Auctions move to &ldquo;Closed&rdquo; automatically after the auction date. Starred auctions show first on the Home page.</div>
                    <NewButton onClick={() => setCreating(true)} testid="admin-new-auction-btn">New Auction</NewButton>
                </div>
                {items.length === 0 ? <EmptyState msg="No auction properties yet."/> : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[860px]">
                            <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                                <th className="pb-3">Property</th><th className="pb-3">City</th><th className="pb-3">Reserve</th><th className="pb-3">Auction Date</th><th className="pb-3">Authority</th><th className="pb-3">Status</th><th className="pb-3">Home</th><th className="pb-3">Actions</th>
                            </tr></thead>
                            <tbody>
                                {items.map((p) => (
                                    <tr key={p.id} className="border-b border-black/8 last:border-b-0" data-testid={`admin-auction-row-${p.slug}`}>
                                        <td className="py-3 font-bold">{p.title}{p.is_sample && <span className="ml-2 text-[9px] uppercase tracking-[0.16em] border border-black/20 px-1.5 py-0.5 text-black/50">Sample</span>}</td>
                                        <td className="py-3 text-black/70">{p.city}</td>
                                        <td className="py-3 font-bold text-[var(--ebg-gold)]">{p.price_display}</td>
                                        <td className="py-3 tabular-nums">{fmtDate(p.auction_date)}</td>
                                        <td className="py-3 text-black/70">{p.auction_authority || "—"}</td>
                                        <td className="py-3"><Badge tone={p.auction_status === "closed" ? "red" : "green"}>{p.auction_status === "closed" ? "Closed" : "Upcoming"}</Badge></td>
                                        <td className="py-3"><StarToggle on={p.featured} onClick={() => feature(p)} testid={`admin-auction-feature-${p.slug}`} title="Feature on Home page"/></td>
                                        <td className="py-3"><div className="flex gap-1">
                                            <button onClick={() => openEdit(p.slug)} data-testid={`admin-auction-edit-${p.slug}`} className={`${btn} border-black/15 hover:border-black`}><Pencil size={11}/> Edit</button>
                                            <button onClick={() => del(p)} data-testid={`admin-auction-delete-${p.slug}`} className={`${btn} border-red-500/40 text-red-600 hover:bg-red-600 hover:text-white`}><Trash2 size={11}/> Delete</button>
                                        </div></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
            {creating && <PropertyForm preset={AUCTION_PRESET} onClose={() => setCreating(false)} onSaved={load}/>}
            {editing && <PropertyForm existing={editing} onClose={() => setEditing(null)} onSaved={load}/>}
        </>
    );
}

export function BrandsPanel() {
    const [items, setItems] = useState([]);
    const [form, setForm] = useState(null);
    const load = () => api.get("/admin/brands").then((r) => setItems(r.data.items || [])).catch(() => {});
    useEffect(() => { load(); }, []);

    const del = async (b) => {
        if (!window.confirm(`Delete brand "${b.name}"?`)) return;
        try { await api.delete(`/admin/brands/${b.id}`); toast.success("Brand deleted"); load(); } catch (e) { toast.error(formatApiError(e)); }
    };
    const feature = async (b) => {
        try { await api.patch(`/admin/brands/${b.id}/featured`, { featured: !b.featured }); load(); } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <>
            <Panel title={`All Brands (${items.length})`} cta={{ to: "/brands", l: "View Public Page" }} testid="admin-brands">
                <div className="flex items-center justify-between mb-4 -mt-2 gap-3 flex-wrap">
                    <div className="text-[11px] text-black/55">Starred brands appear in &ldquo;Featured Brands&rdquo; on the Home page ({items.filter((b) => b.featured).length} active).</div>
                    <NewButton onClick={() => setForm({})} testid="admin-new-brand-btn">New Brand</NewButton>
                </div>
                {items.length === 0 ? <EmptyState msg="No brands yet."/> : (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[820px]">
                            <thead><tr className="text-left text-[10px] uppercase tracking-[0.18em] text-black/50 border-b border-black/10">
                                <th className="pb-3">Brand</th><th className="pb-3">Category</th><th className="pb-3">Opportunity</th><th className="pb-3">Investment</th><th className="pb-3">Visible</th><th className="pb-3">Featured</th><th className="pb-3">Actions</th>
                            </tr></thead>
                            <tbody>
                                {items.map((b) => (
                                    <tr key={b.id} className="border-b border-black/8 last:border-b-0" data-testid={`admin-brand-row-${b.slug}`}>
                                        <td className="py-3"><div className="flex items-center gap-3"><BrandLogo brand={b} size="w-9 h-9" text="text-xs"/><span className="font-bold">{b.name}</span>{b.is_sample && <span className="text-[9px] uppercase tracking-[0.16em] border border-black/20 px-1.5 py-0.5 text-black/50">Sample</span>}</div></td>
                                        <td className="py-3 text-black/70">{b.category}</td>
                                        <td className="py-3 text-black/70">{b.opportunity_type}</td>
                                        <td className="py-3">{b.investment_range || "—"}</td>
                                        <td className="py-3"><Badge tone={b.active ? "green" : "red"}>{b.active ? "Live" : "Hidden"}</Badge></td>
                                        <td className="py-3"><StarToggle on={b.featured} onClick={() => feature(b)} testid={`admin-brand-feature-${b.slug}`} title="Feature on Home page"/></td>
                                        <td className="py-3"><div className="flex gap-1">
                                            <button onClick={() => setForm(b)} data-testid={`admin-brand-edit-${b.slug}`} className={`${btn} border-black/15 hover:border-black`}><Pencil size={11}/> Edit</button>
                                            <button onClick={() => del(b)} data-testid={`admin-brand-delete-${b.slug}`} className={`${btn} border-red-500/40 text-red-600 hover:bg-red-600 hover:text-white`}><Trash2 size={11}/> Delete</button>
                                        </div></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Panel>
            {form && <BrandForm existing={form.id ? form : null} onClose={() => setForm(null)} onSaved={load}/>}
        </>
    );
}

const EMPTY_BRAND = {
    name: "", category: "", opportunity_type: "", tagline: "", description: "", logo: "", cover_image: "", images: [],
    investment_range: "", area_required: "", expected_roi: "", payback_period: "", outlets: "", established: "",
    preferred_locations: [], highlights: [], featured: false, active: true, is_sample: false,
};
const UPLOAD = "/admin/uploads/image?folder=brands";
const toList = (s) => s.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);

function BrandForm({ existing, onClose, onSaved }) {
    const [f, setF] = useState(() => ({ ...EMPTY_BRAND, ...(existing || {}) }));
    const [lists, setLists] = useState(() => ({ loc: (existing?.preferred_locations || []).join(", "), hl: (existing?.highlights || []).join("\n") }));
    const [meta, setMeta] = useState({ categories: [], opportunity_types: [] });
    const [busy, setBusy] = useState(false);
    const set = (patch) => setF((p) => ({ ...p, ...patch }));

    useEffect(() => {
        api.get("/brands/meta").then((r) => {
            setMeta(r.data);
            setF((p) => ({ ...p, category: p.category || r.data.categories[0], opportunity_type: p.opportunity_type || r.data.opportunity_types[0] }));
        }).catch(() => {});
    }, []);

    const save = async () => {
        if (!f.name.trim() || !f.description.trim()) { toast.error("Brand name and description are required"); return; }
        setBusy(true);
        const { id, slug, created_at, ...rest } = f;
        const payload = { ...rest, preferred_locations: toList(lists.loc), highlights: toList(lists.hl) };
        try {
            if (existing) await api.put(`/admin/brands/${existing.id}`, payload);
            else await api.post("/admin/brands", payload);
            toast.success(existing ? "Brand updated" : "Brand created");
            onSaved?.(); onClose?.();
        } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-stretch md:items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-6" data-testid="brand-form-modal">
            <div className="bg-white w-full md:max-w-4xl md:max-h-[92vh] flex flex-col shadow-2xl">
                <header className="flex items-center justify-between px-6 py-4 border-b border-black/10 shrink-0">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[var(--ebg-gold)]">{existing ? "Edit Brand" : "New Brand"}</div>
                        <h2 className="font-serif text-2xl mt-1">{f.name || "Untitled brand"}</h2>
                    </div>
                    <button onClick={onClose} data-testid="brand-form-close" className="w-9 h-9 rounded-full border border-black/15 hover:bg-black hover:text-white flex items-center justify-center"><X size={15}/></button>
                </header>
                <div className="flex-1 overflow-y-auto p-6 min-h-0 space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                        <Field label="Brand Name *" v={f.name} onChange={(v) => set({ name: v })} testid="bf-name"/>
                        <Field label="Tagline" v={f.tagline} onChange={(v) => set({ tagline: v })} testid="bf-tagline"/>
                        <Pick label="Category" v={f.category} onChange={(v) => set({ category: v })} opts={meta.categories} testid="bf-category"/>
                        <Pick label="Opportunity Type" v={f.opportunity_type} onChange={(v) => set({ opportunity_type: v })} opts={meta.opportunity_types} testid="bf-opportunity"/>
                        <Field label="Investment Range" v={f.investment_range} onChange={(v) => set({ investment_range: v })} placeholder="₹45 L – ₹70 L" testid="bf-investment"/>
                        <Field label="Space Required" v={f.area_required} onChange={(v) => set({ area_required: v })} placeholder="800 – 1,500 sq.ft." testid="bf-area"/>
                        <Field label="Expected Returns" v={f.expected_roi} onChange={(v) => set({ expected_roi: v })} placeholder="22 – 28% p.a." testid="bf-roi"/>
                        <Field label="Payback / Lease Tenure" v={f.payback_period} onChange={(v) => set({ payback_period: v })} testid="bf-payback"/>
                        <Field label="Outlets" v={f.outlets} onChange={(v) => set({ outlets: v })} testid="bf-outlets"/>
                        <Field label="Established (Year)" v={f.established} onChange={(v) => set({ established: v })} testid="bf-established"/>
                    </div>
                    <Area label="Description *" v={f.description} onChange={(v) => set({ description: v })} rows={4} testid="bf-description"/>
                    <div className="grid md:grid-cols-2 gap-4">
                        <Area label="Preferred Locations (comma separated)" v={lists.loc} onChange={(v) => setLists((l) => ({ ...l, loc: v }))} rows={2} testid="bf-locations"/>
                        <Area label="Highlights (one per line)" v={lists.hl} onChange={(v) => setLists((l) => ({ ...l, hl: v }))} rows={3} testid="bf-highlights"/>
                    </div>
                    <div className="grid md:grid-cols-2 gap-6">
                        <ImageUploader label="Brand Logo" value={f.logo} onChange={(v) => set({ logo: v })} testid="bf-logo-uploader" endpoint={UPLOAD} hint="Square PNG with transparent background works best."/>
                        <ImageUploader label="Cover Image" value={f.cover_image} onChange={(v) => set({ cover_image: v })} testid="bf-cover-uploader" endpoint={UPLOAD} hint="Landscape photo of a store / outlet."/>
                    </div>
                    <GalleryUploader label="Gallery Images" values={f.images || []} onChange={(v) => set({ images: v })} max={10} testid="bf-gallery-uploader" endpoint={UPLOAD}/>
                    <div className="flex flex-wrap gap-6 pt-2 border-t border-black/8">
                        <Toggle label="Visible on website" v={f.active} onChange={(v) => set({ active: v })} testid="bf-active"/>
                        <Toggle label="Featured on Home page" v={f.featured} onChange={(v) => set({ featured: v })} testid="bf-featured"/>
                        <Toggle label="Sample listing" v={f.is_sample} onChange={(v) => set({ is_sample: v })} testid="bf-sample"/>
                    </div>
                </div>
                <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-black/10 shrink-0">
                    <button onClick={onClose} className="text-[11px] uppercase tracking-[0.22em] font-bold text-black/60 hover:text-black px-4" data-testid="bf-cancel">Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="bf-save" className="bg-black text-white px-6 py-3 text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-[var(--ebg-gold)] hover:text-black disabled:opacity-50 inline-flex items-center gap-2">
                        {busy ? "Saving…" : existing ? "Update Brand" : "Publish Brand"} <Check size={13}/>
                    </button>
                </footer>
            </div>
        </div>
    );
}

const lbl = "text-[10px] uppercase tracking-[0.22em] font-bold text-black/55 mb-1.5";
const inp = "w-full border border-black/15 px-3 py-2.5 text-sm outline-none focus:border-[var(--ebg-gold)] bg-white";
function Field({ label, v, onChange, placeholder, testid }) {
    return <label className="block"><div className={lbl}>{label}</div><input data-testid={testid} value={v || ""} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} className={inp}/></label>;
}
function Area({ label, v, onChange, rows, testid }) {
    return <label className="block"><div className={lbl}>{label}</div><textarea data-testid={testid} value={v || ""} rows={rows} onChange={(e) => onChange(e.target.value)} className={inp}/></label>;
}
function Pick({ label, v, onChange, opts, testid }) {
    return <label className="block"><div className={lbl}>{label}</div><select data-testid={testid} value={v || ""} onChange={(e) => onChange(e.target.value)} className={inp}>{opts.map((o) => <option key={o} value={o}>{o}</option>)}</select></label>;
}
function Toggle({ label, v, onChange, testid }) {
    return <label className="flex items-center gap-2 text-[12px] font-bold cursor-pointer"><input type="checkbox" checked={!!v} onChange={(e) => onChange(e.target.checked)} data-testid={testid} className="w-4 h-4 accent-[var(--ebg-gold)]"/>{label}</label>;
}
